import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ChevronLeft, Folder, FileText, ClipboardList, Info, Lock, Upload, Loader2, Trash2, ExternalLink, Download, CalendarDays, Map as MapIcon, Utensils, Archive } from 'lucide-react';
import InventoryTable from './InventoryTable';
import PresentationsButton from './PresentationsButton';
import BrandStandardsButton from './BrandStandardsButton';
import WorkspaceSavedFiles from './WorkspaceSavedFiles';
import DepartmentScheduleDialog from './DepartmentScheduleDialog';
import DepartmentRoadmapDialog from './DepartmentRoadmapDialog';
import RestaurantDetailDialog from './RestaurantDetailDialog';
import OSEListsAggregator from './OSEListsAggregator';
import InventoryDialog from './InventoryDialog';
import PropertyBudgetsDialog from './PropertyBudgetsDialog';
import DepartmentRoadmapStrip from './DepartmentRoadmapStrip';
import { useChecklistCreator } from '../contexts/ChecklistContext';
import TrainingCreatorButton from './TrainingCreatorButton';
import ExportButton from './ExportButton';
import WorkspaceLibraryPanel from './WorkspaceLibraryPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* iter-121 — per-workspace accent colors. Used for the root Library
 * panel header strip + buttons so each department feels distinct. */
const DEPT_ACCENTS = {
  'grounds':              '#2E7D32',  // green
  'it-projects':          '#1976D2',  // blue
  'hr-projects':          '#7B1FA2',  // purple
  'engineering':          '#E65100',  // orange
  'finance':              '#1F4FBE',  // royal-blue
  'brand-standards':      '#003DA5',  // hyatt blue
  'procurement-planning': '#5D4037',  // brown
  'fb-workspace':         '#C62828',  // red
  'rooms':                '#0D9488',  // teal
  'spa':                  '#9C27B0',  // violet
  'security':             '#37474F',  // slate
  'entertainment':        '#D81B60',  // pink
  'water-sports':         '#0288D1',  // cyan
  'sales':                '#00695C',  // dark teal
  'laundry':              '#6A1B9A',  // dark purple
  'ose-lists':            '#5E35B1',  // deep purple
  'storage':              '#455A64',  // blue grey
  'designs':              '#FF6F59',  // coral
};

function deptAccent(rootId) { return DEPT_ACCENTS[rootId] || '#1F4FBE'; }

function fileExt(name) { return (name?.split('.').pop() || '').toLowerCase(); }
function isImage(ext) { return ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext); }

export default function WorkspaceDialog({ open, onClose, rootId }) {
  const [tree, setTree] = useState([]);
  const [currentId, setCurrentId] = useState(rootId);
  const [authGateFor, setAuthGateFor] = useState(null); // node id that needs login
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  // Singleton checklist creator (lives at App level — see
  // contexts/ChecklistContext.jsx for the rationale on why this is NOT
  // mounted inline inside this Dialog tree).
  const checklist = useChecklistCreator();
  // Admin-only branches (HR, Finance, Property Budgets, IT, Designs &
  // Visualisations) are gated to admin and engineer ONLY. Viewers and
  // editors see a padlock placeholder just like guests do — full access
  // is reserved for reviewers/leadership.
  const userRole = typeof window !== 'undefined' ? (localStorage.getItem('user_role') || '') : '';
  const hasFullAccess = ['admin', 'engineer'].includes(userRole);

  useEffect(() => { if (open) setCurrentId(rootId); }, [open, rootId]);

  // Schedule + Roadmap dialogs (department-level, root only).
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [roadmapOpen, setRoadmapOpen] = useState(false);
  // iter-79: a Finance-only deep link to Property Budgets. Lives here
  // so the workspace tree doesn't have to know about a Budgets node
  // — we just render a launcher button when the user is at the
  // Finance root. Saves a click vs hunting through the workspace.
  const [propertyBudgetsOpen, setPropertyBudgetsOpen] = useState(false);

  // F&B restaurant concepts — fetched when the F&B root OR the FOH child
  // node is open so we can render the 11 concept tiles inline (mirrors the
  // public landing page). The user requested the same grid appear under
  // F&B > FOH for staff-facing scheduling/roadmaps.
  const [restaurants, setRestaurants] = useState([]);
  const [activeRestaurant, setActiveRestaurant] = useState(null);
  // When the user clicks a card in the OS&E Lists aggregator we open the
  // InventoryTable scoped to that list's REAL node_id. Set to a
  // `{node_id, node_label}` shape — null when closed.
  const [aggregatedInventory, setAggregatedInventory] = useState(null);
  const showRestaurantGrid = currentId === 'fb-workspace' || currentId === 'fb-workspace.foh';
  useEffect(() => {
    if (!showRestaurantGrid) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/property`)
      .then(({ data }) => setRestaurants(data?.fnb || []))
      .catch(() => setRestaurants([]));
  }, [showRestaurantGrid]);

  useEffect(() => {
    if (!open) return;
    const auth = adminToken ? { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true } : {};
    axios.get(`${API}/workspace/tree`, auth).then(({ data }) => setTree(data)).catch(() => setTree([]));
  }, [open, adminToken]);

  const nodesById = useMemo(() => {
    const m = {};
    tree.forEach((n) => { m[n.id] = n; });
    return m;
  }, [tree]);

  const current = nodesById[currentId];
  const children = useMemo(
    () => tree.filter((n) => n.parent_id === currentId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [tree, currentId],
  );

  // Document counts per child node — drives the small badge on each
  // file-category card. Refetched whenever the user navigates to a
  // new hub level so newly-uploaded docs reflect immediately.
  const [docCounts, setDocCounts] = useState({});
  useEffect(() => {
    if (!currentId) return;
    let cancelled = false;
    axios
      .get(`${API}/workspace/nodes/${currentId}/document-counts`)
      .then(({ data }) => { if (!cancelled) setDocCounts(data || {}); })
      .catch(() => { if (!cancelled) setDocCounts({}); });
    return () => { cancelled = true; };
  }, [currentId]);

  // Cross-reference items: when this hub has a `dept_tag`, surface every
  // node from elsewhere in the tree that's tagged for this department.
  // (e.g. F&B hub picks up `ose-lists.banquets`, all `ose-lists.fb.*`, etc.)
  //
  // SPECIAL CASE — the global OS&E Lists hub (id `ose-lists`) auto-mirrors
  // every `inventory`-type node in the entire tree (e.g. `spa.inventories`,
  // `security.inventories`, `rooms.housekeeping.inventories`) so any new
  // departmental inventory appears here too — zero tag maintenance.
  const mirroredItems = useMemo(() => {
    if (!current) return [];
    const directChildIds = new Set(children.map((c) => c.id));
    const taggedMatches = current.dept_tag
      ? tree.filter(
          (n) =>
            Array.isArray(n.tags) &&
            n.tags.includes(current.dept_tag) &&
            !directChildIds.has(n.id) &&
            n.id !== currentId,
        )
      : [];
    let autoOse = [];
    if (currentId === 'ose-lists') {
      autoOse = tree.filter(
        (n) =>
          n.type === 'inventory' &&
          !directChildIds.has(n.id) &&
          // Skip the hub's own grandchildren (already shown via direct nav)
          (n.parent_id || '') !== 'ose-lists' &&
          (n.parent_id || '').startsWith('ose-lists.') === false,
      );
    }
    // De-dup by id since the two sources can overlap.
    const dedupe = new Map();
    [...taggedMatches, ...autoOse].forEach((n) => dedupe.set(n.id, n));
    return Array.from(dedupe.values()).sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [current, tree, children, currentId]);

  // Breadcrumb: walk up parent chain until we reach the root of this dialog
  const crumbs = useMemo(() => {
    const list = [];
    let n = current;
    while (n) {
      list.unshift(n);
      if (n.id === rootId) break;
      n = nodesById[n.parent_id];
    }
    return list;
  }, [current, rootId, nodesById]);

  const handleTileClick = (node) => {
    // Admin-only branches: gate by role, not just token. Viewer + editor
    // still see the padlock and the auth-gate prompt — leadership reviews
    // the locked content (HR / Finance / Budgets / IT / Designs).
    if (node.admin_only && !hasFullAccess) {
      setAuthGateFor(node);
      return;
    }
    setCurrentId(node.id);
  };

  const goToAdmin = () => {
    // Remember intent so after login we could come back — simple redirect for now
    window.location.href = '/admin';
  };

  if (!current) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md p-6"><div className="text-sm">Loading…</div></DialogContent>
      </Dialog>
    );
  }

  const titleText = current.id === rootId ? current.label : current.label;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid={`workspace-dialog-${rootId}`}
        className="w-[95vw] max-w-5xl max-h-[92vh] overflow-y-auto p-0"
        style={{ backgroundColor: '#F4F1EB' }}
        // On mobile, users tap outside the input to dismiss the keyboard.
        // Radix's default click-outside behavior would close this dialog
        // mid-edit — wiping the row the user was filling out. Block both
        // pointer-down-outside and Escape so closing is explicit (the
        // top-right × button + Back chip both work).
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="p-5 sm:p-6">
          <DialogHeader className="mb-4">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1 flex-wrap mb-2">
              {currentId !== rootId && (
                <button
                  onClick={() => setCurrentId(nodesById[current.parent_id]?.id || rootId)}
                  className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold text-white hover:-translate-y-0.5 transition-all"
                  style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
                  data-testid="workspace-back"
                >
                  <ChevronLeft className="w-3 h-3" /> Back
                </button>
              )}
              {crumbs.map((c, idx) => (
                <span key={c.id} className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentId(c.id)}
                    className="text-[11px] font-bold hover:underline"
                    style={{ fontFamily: 'Manrope', color: idx === crumbs.length - 1 ? '#003DA5' : '#7A8C8C' }}
                  >
                    {c.label}
                  </button>
                  {idx < crumbs.length - 1 && <span style={{ color: '#A3C4EE' }}>›</span>}
                </span>
              ))}
            </div>

            <DialogTitle className="text-2xl sm:text-3xl flex items-center gap-2 flex-wrap" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
              {current.type === 'inventory' ? <ClipboardList className="w-6 h-6" style={{ color: '#003DA5' }} />
                : current.type === 'hub' || current.type === 'info' ? <Folder className="w-6 h-6" style={{ color: '#003DA5' }} />
                : <FileText className="w-6 h-6" style={{ color: '#003DA5' }} />}
              {titleText}
              {current.admin_only && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}>
                  <Lock className="w-2.5 h-2.5" /> Admin
                </span>
              )}
              {/* The Presentations button + (when applicable) the brand
                  PDF download still gate behind currentId===rootId. The
                  Create-checklist affordance, by contrast, is hoisted to
                  EVERY level of the workspace tree (root, hub, leaf) so
                  the GM can build a checklist while reviewing any
                  document. Editor-or-higher (anyone with adminToken) can
                  click it — the export endpoint already enforces editor+
                  on the backend. */}
              {currentId === rootId && (
                <span className="ml-auto flex items-center gap-2 flex-wrap">
                  <PresentationsButton deptId={rootId} deptLabel={current.label} />
                  {rootId === 'brand-standards' && <BrandStandardsButton />}
                </span>
              )}
              {adminToken && (
                <button
                  data-testid="workspace-checklist-btn"
                  onClick={() => checklist.open({ workspaceLabel: current?.label || '', adminToken })}
                  className={`${currentId === rootId ? '' : 'ml-auto'} inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold text-white transition hover:-translate-y-0.5`}
                  style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)', fontFamily: 'Manrope' }}
                  title="Build a Hyatt-branded checklist for this department"
                >
                  <ClipboardList className="w-3 h-3" /> Create checklist
                </button>
              )}
              {adminToken && currentId === rootId && (
                <>
                  <button
                    data-testid="workspace-schedule-btn"
                    onClick={() => setScheduleOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold text-white transition hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', fontFamily: 'Manrope' }}
                    title="Generate an AI-driven 7-day shift schedule"
                  >
                    <CalendarDays className="w-3 h-3" /> AI Schedule
                  </button>
                  <button
                    data-testid="workspace-roadmap-btn"
                    onClick={() => setRoadmapOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold text-white transition hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #15803D 0%, #22C55E 100%)', fontFamily: 'Manrope' }}
                    title="Department roadmap (milestones)"
                  >
                    <MapIcon className="w-3 h-3" /> Roadmap
                  </button>
                  {/* iter-79: Finance root gets a direct link to the
                      Property Budgets dialog so admins don't have to
                      hunt for it. Other roots get the standard
                      schedule + roadmap controls only. */}
                  {currentId === 'finance' && (
                    <button
                      data-testid="workspace-property-budgets-btn"
                      onClick={() => setPropertyBudgetsOpen(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold text-white transition hover:-translate-y-0.5"
                      style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #0369A1 100%)', fontFamily: 'Manrope' }}
                      title="Open the full Property Budgets center (CapEx, Builder, AI Analysis, Documents)"
                    >
                      💼 Property Budgets
                    </button>
                  )}
                </>
              )}
            </DialogTitle>
            {current.info_text && (
              <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: 'white', border: '1px solid #D9E3F1' }}>
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#003DA5' }} />
                <p className="text-xs" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{current.info_text}</p>
              </div>
            )}

            {/* Consolidated Saved Files — hoisted into the department
                header so every view (hub / leaf / mirror) gets the same
                snapshot folder without scrolling to the bottom. Default
                collapsed; click to expand. Hidden on the special-case
                OS&E Lists hub since that page is intentionally "OS&E
                lists and nothing else". */}
            {adminToken && currentId !== 'ose-lists' && (
              <div className="mt-3" data-testid="workspace-saved-files-header-slot">
                <WorkspaceSavedFiles currentId={current.id} tree={tree} defaultCollapsed />
              </div>
            )}
          </DialogHeader>

          {/* iter-121 — Library panel: pinned at the root of every
              workspace. Pictures · Documents · Links. Editor-tier can
              upload + delete; others see read-only. The OS&E aggregator
              and Designs workspace already have their own libraries so
              we skip them. */}
          {currentId === rootId && rootId !== 'ose-lists' && rootId !== 'designs' && (
            <WorkspaceLibraryPanel
              rootId={rootId}
              label={current.label}
              accent={deptAccent(rootId)}
            />
          )}

          {/* Content — every leaf surfaces BOTH a documents gallery and an inventory form so admins can upload AND fill the 6-column form regardless of seeded type. Hubs still show child tiles. */}
          {currentId === 'ose-lists' ? (
            // Special-case the OS&E Lists hub — it MUST show only the
            // aggregated list of every department's OS&E lists and
            // absolutely nothing else (no department children, no F&B
            // tiles, no mirrored items, no Saved Files block). This
            // matches the user's spec: "OS&E Lists button … should only
            // have OS&E lists from all the departments within the
            // platform and absolutely nothing else."
            <OSEListsAggregator
              onOpenList={(li) => setAggregatedInventory({ node_id: li.node_id, node_label: `${li.node_label} → ${li.name}` })}
            />
          ) : (current.type === 'hub' || current.type === 'info') ? (
            <>
              <HubGrid items={children} onClick={handleTileClick} hasFullAccess={hasFullAccess} docCounts={docCounts} />

              {/* 11 dining concept tiles — shown at F&B root AND under
                  F&B > FOH. Mirrors the public landing page; each tile
                  opens the same RestaurantDetailDialog regardless of
                  where it's clicked (data is mirrored via
                  `restaurant.{id}` dept-scope). */}
              {showRestaurantGrid && restaurants.length > 0 && (
                <div className="mt-6 pt-5" style={{ borderTop: '1px solid #D9E3F1' }}>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-3 flex items-center gap-2" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                    <Utensils className="w-3 h-3" />
                    Eleven Dining Concepts ({restaurants.length})
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="fb-concept-grid">
                    {restaurants.map((r) => {
                      const accent = r.accent_color || '#003DA5';
                      return (
                        <button
                          key={r.id}
                          type="button"
                          data-testid={`fb-concept-tile-${r.id}`}
                          onClick={() => setActiveRestaurant(r)}
                          className="text-left rounded-xl overflow-hidden transition hover:-translate-y-0.5 hover:shadow-lg"
                          style={{ backgroundColor: 'white', border: `1px solid ${accent}30`, fontFamily: 'Manrope' }}
                        >
                          <div className="aspect-[5/3] flex items-center justify-center p-3" style={{ backgroundColor: `${accent}10` }}>
                            {r.logo_url ? (
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${r.logo_url}`} alt={`${r.name} logo`} className="max-h-full max-w-full object-contain"/>
                            ) : (
                              <Utensils className="w-8 h-8" style={{ color: accent }} />
                            )}
                          </div>
                          <div className="px-2.5 py-2 border-t" style={{ borderColor: `${accent}20` }}>
                            <p className="text-xs font-bold leading-tight" style={{ color: '#1A2E2E' }}>{r.name}</p>
                            {r.detail && <p className="text-[10px] text-gray-500 truncate mt-0.5">{r.detail}</p>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {mirroredItems.length > 0 && (
                <div className="mt-6 pt-5" style={{ borderTop: '1px solid #D9E3F1' }}>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-3 flex items-center gap-2" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                    <ClipboardList className="w-3 h-3" />
                    Mirrored from OS&amp;E Lists ({mirroredItems.length})
                  </p>
                  <HubGrid items={mirroredItems} onClick={handleTileClick} hasFullAccess={hasFullAccess} docCounts={docCounts} mirrored />
                </div>
              )}
              {/* The per-department Saved Files panel used to live here,
                  at the bottom of the page. It was hoisted into the
                  DialogHeader (above the children grid) in May 2026 so
                  every view (hub / leaf / mirror) gets the same
                  consolidated snapshot folder without scrolling. */}
              {/* iter-79: at every dept ROOT screen we anchor a compact
                  roadmap strip at the bottom — same visual rhythm as
                  the main dashboard's Reconstruction Roadmap. The full
                  editor still lives behind the "Roadmap" header pill. */}
              {currentId === rootId && (
                <DepartmentRoadmapStrip
                  deptId={rootId}
                  deptLabel={(tree.find((n) => n.id === rootId) || {}).label || rootId}
                  onOpenEditor={() => setRoadmapOpen(true)}
                />
              )}
            </>
          ) : current.mirror_area_id ? (
            <MirrorView nodeId={current.id} />
          ) : (
            <LeafContent nodeId={current.id} nodeLabel={current.label} initialTab={current.type === 'inventory' ? 'inventory' : 'documents'} />
          )}
        </div>
      </DialogContent>

      {/* Auth gate modal */}
      {authGateFor && (
        <Dialog open onOpenChange={() => setAuthGateFor(null)}>
          <DialogContent className="max-w-sm p-6" data-testid="workspace-auth-gate">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-5 h-5" style={{ color: '#003DA5' }} />
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>Admin access required</h3>
            </div>
            <p className="text-sm mb-5" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
              <span className="font-bold">{authGateFor.label}</span> contains confidential information
              and is visible to administrators only. Sign in with your admin password to continue.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setAuthGateFor(null)} className="flex-1 px-3 py-2 rounded-lg text-xs font-bold"
                style={{ border: '1px solid #D9E3F1', color: '#4A5D5D', fontFamily: 'Manrope', backgroundColor: 'white' }}>
                Cancel
              </button>
              <button onClick={goToAdmin} className="flex-1 px-3 py-2 rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }} data-testid="workspace-goto-admin">
                Sign in as admin
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <DepartmentScheduleDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        deptId={rootId}
        deptLabel={(tree.find((n) => n.id === rootId) || {}).label || rootId}
      />
      <DepartmentRoadmapDialog
        open={roadmapOpen}
        onClose={() => setRoadmapOpen(false)}
        deptId={rootId}
        deptLabel={(tree.find((n) => n.id === rootId) || {}).label || rootId}
        canEdit={hasFullAccess}
      />
      <PropertyBudgetsDialog
        open={propertyBudgetsOpen}
        onClose={() => setPropertyBudgetsOpen(false)}
      />
      <RestaurantDetailDialog
        outlet={activeRestaurant}
        open={!!activeRestaurant}
        onClose={() => setActiveRestaurant(null)}
        canEdit={hasFullAccess}
        onUpdated={(next) => {
          if (!next) return;
          setRestaurants((arr) => arr.map((r) => (r.id === next.id ? next : r)));
          setActiveRestaurant(next);
        }}
      />

      {/* Aggregated OS&E view → opening a card from the directory pops
          the InventoryTable scoped to that list's real node_id so the
          user lands directly on the editable form. */}
      <InventoryDialog
        open={!!aggregatedInventory}
        onClose={() => setAggregatedInventory(null)}
        nodeId={aggregatedInventory?.node_id}
        nodeLabel={aggregatedInventory?.node_label}
      />

      {/* Hyatt-branded checklist builder is mounted globally (App.js
          via ChecklistCreatorProvider) — opened via the
          useChecklistCreator() hook above. Don't mount it inline here:
          Radix Dialog would treat clicks on it as outside-clicks and
          close THIS dialog before the export request can fire. */}
    </Dialog>
  );
}

function LeafContent({ nodeId, nodeLabel, initialTab }) {
  const [tab, setTab] = useState(initialTab || 'documents');
  return (
    <div>
      {/* Tabs — every leaf gets both: Documents (uploads) + Form (6-column inventory) */}
      <div className="flex gap-1 p-1 rounded-full mb-4 inline-flex" style={{ backgroundColor: '#F0F4FA' }}>
        <LeafTab active={tab === 'documents'} label="Documents" onClick={() => setTab('documents')} testid={`leaf-tab-docs-${nodeId}`} />
        <LeafTab active={tab === 'inventory'} label="Form / Inventory" onClick={() => setTab('inventory')} testid={`leaf-tab-form-${nodeId}`} />
      </div>
      {tab === 'documents' ? <DocumentsGallery nodeId={nodeId} /> : <InventoryTable nodeId={nodeId} nodeLabel={nodeLabel} />}
    </div>
  );
}

function LeafTab({ active, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="px-4 py-1.5 rounded-full text-[11px] font-bold transition-all"
      style={{
        backgroundColor: active ? '#003DA5' : 'transparent',
        color: active ? 'white' : '#4A5D5D',
        fontFamily: 'Manrope',
      }}
    >
      {label}
    </button>
  );
}

function HubGrid({ items, onClick, hasFullAccess, docCounts = {}, mirrored = false }) {
  if (!items.length) {
    return <div className="py-12 text-center text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Nothing here yet.</div>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      {items.map((n) => {
        const locked = n.admin_only && !hasFullAccess;
        const Icon = n.type === 'inventory' ? ClipboardList : n.type === 'hub' ? Folder : FileText;
        const count = docCounts[n.id] || 0;
        return (
          <button
            key={n.id}
            onClick={() => onClick(n)}
            data-testid={`workspace-tile-${n.id}`}
            className="group relative rounded-xl p-4 text-left transition-all hover:-translate-y-1 hover:shadow-lg"
            style={{
              backgroundColor: locked ? '#F0F4FA' : 'white',
              border: mirrored ? '1px dashed #A3C4EE' : '1px solid #D9E3F1',
              minHeight: 96,
            }}
          >
            {mirrored && (
              <span className="absolute top-2 right-2 text-[8px] font-bold tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope' }}>
                OS&E
              </span>
            )}
            {/* Saved documents count badge — small Apple-style pill on
                every card so admins see at a glance how many files
                live inside each category without opening the tile. */}
            {!mirrored && count > 0 && (
              <span
                className="absolute top-2 right-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full"
                style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope' }}
                title={`${count} saved document${count === 1 ? '' : 's'}`}
                data-testid={`workspace-tile-count-${n.id}`}
              >
                {count}
              </span>
            )}
            <div className="flex items-start gap-2 mb-2">
              <Icon className="w-5 h-5 flex-shrink-0" style={{ color: '#003DA5' }} />
              {locked && <Lock className="w-3.5 h-3.5" style={{ color: '#003DA5' }} />}
            </div>
            <div className="text-[13px] font-bold leading-tight" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
              {n.label}
            </div>
            <div className="mt-1 text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              {n.type === 'inventory' ? 'Inventory' : n.type === 'hub' ? 'Folder' : 'Documents'}
              {count > 0 && !mirrored && <span className="ml-1 normal-case tracking-normal" style={{ color: '#003DA5' }}>· {count} file{count === 1 ? '' : 's'}</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Read-only gallery for Designs nodes that mirror a map area. Pulls
// `before_image`, `after_image`, `photos`, and `renders` from the area and
// renders them as a single Pinterest-style grid so the same renders shown in
// the widget hover popup show up here too.
function MirrorView({ nodeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;

  useEffect(() => {
    const auth = adminToken ? { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true } : {};
    setLoading(true);
    axios
      .get(`${API}/workspace/nodes/${nodeId}/mirror-assets`, auth)
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [nodeId, adminToken]);

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" style={{ color: '#003DA5' }} /></div>;
  if (!data) return <div className="py-12 text-center text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Could not load area assets.</div>;

  // Group assets in user-priority order: renders > photos > before/after.
  const groups = [
    { label: 'Renders', items: data.renders || [], badge: '#003DA5' },
    { label: 'Photos', items: data.photos || [], badge: '#7A8C8C' },
    {
      label: 'Before & After',
      items: [
        data.before_image && { url: data.before_image, kind: 'Before' },
        data.after_image && { url: data.after_image, kind: 'After' },
      ].filter(Boolean),
      badge: '#1F4FBE',
    },
  ];
  const total = groups.reduce((s, g) => s + g.items.length, 0);

  return (
    <div>
      <div className="mb-4 flex items-start gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FAF8F3', border: '1px solid #EAE5D9' }}>
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#003DA5' }} />
        <p className="text-xs" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
          Showing renders &amp; photos from the <strong>{data.area_name}</strong> map widget. Edit them via the map's widget editor — changes here are read-only mirrors so the visualisations stay in sync.
        </p>
      </div>
      {total === 0 ? (
        <div className="py-12 text-center text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
          No images yet. Upload them through the map's widget editor.
        </div>
      ) : (
        groups.map((g) => g.items.length > 0 && (
          <div key={g.label} className="mb-6">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: g.badge, fontFamily: 'Manrope' }}>
              {g.label} · {g.items.length}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {g.items.map((it, i) => {
                const url = typeof it === 'string' ? it : it.url;
                const kind = typeof it === 'string' ? null : it.kind;
                return (
                  <a
                    key={url || i}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="block rounded-xl overflow-hidden relative transition-all hover:-translate-y-0.5"
                    style={{ backgroundColor: 'white', border: '1px solid #D9E3F1' }}
                    data-testid={`mirror-asset-${nodeId}-${i}`}
                  >
                    <img src={url} alt="" className="aspect-square w-full object-cover" />
                    {kind && (
                      <span className="absolute top-2 left-2 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope' }}>
                        {kind}
                      </span>
                    )}
                  </a>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function DocumentsGallery({ nodeId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;

  const load = async () => {
    setLoading(true);
    const auth = adminToken ? { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true } : {};
    try {
      const { data } = await axios.get(`${API}/workspace/nodes/${nodeId}/documents`, auth);
      setDocs(data);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [nodeId]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !adminToken) return;
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('keep_raw', 'true');
        // eslint-disable-next-line no-await-in-loop
        const { data: up } = await axios.post(`${API}/admin/upload`, fd, {
          headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        });
        // iter-122 — Multi-file workspace upload. Single keeps the title
        // prompt; batch uses filename as title.
        const title = files.length === 1
          ? (window.prompt('Title:', file.name.replace(/\.[^.]+$/, '')) || file.name)
          : file.name.replace(/\.[^.]+$/, '');
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/admin/workspace/documents`, {
          node_id: nodeId,
          title,
          file_url: `${BACKEND}${up.url}`,
          file_name: file.name,
          file_type: (file.name.split('.').pop() || '').toLowerCase(),
          order: docs.length + i,
        }, { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true });
        ok += 1;
      } catch (err) {
        console.error('Workspace upload failed for', file.name, err);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    if (ok < files.length) alert(`Uploaded ${ok}/${files.length}. Some failed.`);
    load();
  };

  const handleDelete = async (d) => {
    if (!adminToken || !window.confirm(`Permanently delete "${d.title}"? Pick Archive instead if you might want it back.`)) return;
    try {
      await axios.delete(`${API}/admin/workspace/documents/${d.id}`, { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true });
      load();
    } catch (err) { alert(err?.response?.data?.detail || 'Delete failed'); }
  };

  // Archive — soft-hides the document. Hidden from this view but
  // recoverable from Admin → Archive.
  const handleArchive = async (d) => {
    if (!adminToken) return;
    try {
      await axios.put(`${API}/admin/archive/workspace_doc/${d.id}`, {}, { headers: { Authorization: `Bearer ${adminToken}` }, withCredentials: true });
      load();
    } catch (err) { alert(err?.response?.data?.detail || 'Archive failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold tracking-[0.15em] uppercase" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
          {docs.length} {docs.length === 1 ? 'document' : 'documents'}
        </div>
        {adminToken && (
          <div className="flex items-center gap-2">
            {/* iter-82: Universal export of the current document listing.
                Visible to editors+ on any documents folder; the rows are
                whatever the gallery is rendering. */}
            <ExportButton
              title={`Atrium · ${nodeId || 'Documents'}`}
              rows={docs}
              columns={[
                { key: 'title',       label: 'Title' },
                { key: 'description', label: 'Description' },
                { key: 'category',    label: 'Category' },
                { key: 'created_by',  label: 'Uploaded by' },
                { key: 'created_at',  label: 'Uploaded at' },
                { key: 'url',         label: 'URL' },
              ]}
            />
            {/* iter-82: AI-powered Training Pack generator — visible only
                on the HR · Training folder. Other nodes get the normal
                Upload button only. */}
            {nodeId === 'hr-projects.training' && (
              <TrainingCreatorButton onCreated={() => load()} />
            )}
            <input ref={fileRef} type="file" multiple className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.dwg,.dxf,.txt" onChange={handleUpload} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition-all disabled:opacity-50"
              style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
              data-testid="workspace-doc-upload"
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Upload
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
        </div>
      ) : docs.length === 0 ? (
        <div className="py-12 text-center text-sm rounded-xl" style={{ backgroundColor: 'white', border: '1px dashed #A3C4EE', color: '#7A8C8C', fontFamily: 'Manrope' }}>
          {adminToken ? 'No documents yet. Click “Upload” above.' : 'No documents uploaded yet.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {docs.map((d) => {
            const ext = fileExt(d.file_name || d.file_url);
            const img = isImage(ext);
            return (
              <div key={d.id} className="group relative rounded-xl overflow-hidden" style={{ backgroundColor: 'white', border: '1px solid #D9E3F1' }} data-testid={`workspace-doc-${d.id}`}>
                <a href={d.file_url} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="aspect-[4/3] flex items-center justify-center" style={{ backgroundColor: '#EAE5D9' }}>
                    {img ? <img src={d.file_url} alt={d.title} className="w-full h-full object-cover" />
                      : <div className="flex flex-col items-center gap-1"><FileText className="w-10 h-10" style={{ color: '#003DA5' }} /><span className="text-[10px] font-bold uppercase" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>.{ext}</span></div>}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-bold truncate" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{d.title}</p>
                    <div className="flex items-center gap-2 mt-1 text-[10px]" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
                      <span className="flex items-center gap-0.5 font-bold">Open <ExternalLink className="w-2.5 h-2.5" /></span>
                      <span>·</span>
                      <a href={d.file_url} download onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 font-bold">
                        Download <Download className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  </div>
                </a>
                {adminToken && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.preventDefault(); handleArchive(d); }}
                      className="w-7 h-7 rounded-full flex items-center justify-center shadow-md"
                      style={{ backgroundColor: '#F59E0B', color: 'white' }}
                      title="Archive — restore later from Admin → Archive"
                      data-testid={`workspace-doc-archive-${d.id}`}
                    >
                      <Archive className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => { e.preventDefault(); handleDelete(d); }}
                      className="w-7 h-7 rounded-full flex items-center justify-center shadow-md"
                      style={{ backgroundColor: '#EF4444', color: 'white' }}
                      title="Permanently delete"
                      data-testid={`workspace-doc-delete-${d.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
